import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { calculateWeightProgress } from "../lib/weightUtils";
import { clientsAPI, dashboardAPI, feedbackAPI, photosAPI, csvImportAPI, attendanceAPI, revenueAPI } from "../lib/api";
import api from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  Users, UserCheck, AlertCircle, Plus, LogOut, 
  Search, ChevronRight, Scale, Target, Calendar, Trash2, Edit,
  Crown, Clock, MessageSquare, Archive, Gift, Check, Sparkles,
  Camera, LineChart, FileText, Share2, Ruler, Heart, ClipboardList,
  Activity, Upload, X, Image, CreditCard, Star, Phone, Mail, Zap, BarChart3,
  CheckCircle2, XCircle, Timer, IndianRupee, TrendingUp, Bell
} from "lucide-react";
import { getImageUrl, getInitials } from "../lib/avatarUtils";
import { Avatar } from "../components/Avatar";

export default function DashboardPage() {
  const { trainer, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Redirect admin users to admin dashboard
  useEffect(() => {
    if (isAdmin) {
      navigate("/admin", { replace: true });
    }
  }, [isAdmin, navigate]);

  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total_clients: 0, active_clients: 0, archived_clients: 0, clients_needing_update: 0, client_limit: 10, can_add_more: true, plan_type: "beta" });
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // client to delete
  const [deleting, setDeleting] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(null); // client to archive
  const [archiving, setArchiving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedClients, setArchivedClients] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({ message: "", category: "general" });
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const csvFileRef = useRef(null);
  
  // Batch Attendance state
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [batchMarking, setBatchMarking] = useState(false);
  
  // Revenue Dashboard state
  const [revenueData, setRevenueData] = useState(null);

  // New client form state
  const [newClient, setNewClient] = useState({
    name: "",
    age: "",
    gender: "male",
    starting_weight: "",
    current_weight: "",
    height: "",
    target_weight: "",
    goal: "fat_loss",
    target_duration_weeks: "",
    plan_duration_weeks: "",
    start_date: new Date().toISOString().split('T')[0],
    phone: "",
    email: "",
    joining_date: new Date().toISOString().split('T')[0],
    medical_conditions: "",
    injuries: "",
    coach_notes: "",
    current_body_fat: "",
    target_body_fat: "",
    activity_level: "",
    client_type: "",
    priority: "normal",
    communication_pref: [],
    plan_price: "",
    discount: "",
    payment_status: "",
    payment_mode: "",
    payment_notes: ""
  });
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [beforePhotoPreview, setBeforePhotoPreview] = useState(null);
  const [beforePhotoFile, setBeforePhotoFile] = useState(null);
  const profilePhotoRef = useRef(null);
  const beforePhotoRef = useRef(null);

  useEffect(() => {
    loadData();
    // Request notification permission on first load (non-intrusive)
    if ('Notification' in window && Notification.permission === 'default') {
      const timer = setTimeout(() => {
        Notification.requestPermission();
      }, 5000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty: loadData is stable, runs once on mount

  const loadData = async () => {
    try {
      const [clientsRes, statsRes, remindersRes, archivedRes, attendanceRes, revenueRes] = await Promise.all([
        clientsAPI.getAll(),
        dashboardAPI.getStats(),
        dashboardAPI.getReminders(),
        clientsAPI.getArchived(),
        attendanceAPI.getToday().catch(() => ({ data: null })),
        revenueAPI.getDashboard().catch(() => ({ data: null })),
      ]);
      setClients(clientsRes.data);
      setStats(statsRes.data);
      setReminders(remindersRes.data);
      setArchivedClients(archivedRes.data);
      setTodayAttendance(attendanceRes.data);
      setRevenueData(revenueRes.data);
      
      // Show welcome popup for new users (0 clients, first time)
      if (clientsRes.data.length === 0 && !localStorage.getItem('ft_welcome_shown')) {
        setTimeout(() => setShowWelcomePopup(true), 800);
        localStorage.setItem('ft_welcome_shown', 'true');
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = async (e) => {
    e.preventDefault();
    
    if (!newClient.name || !newClient.age || !newClient.starting_weight) {
      toast.error("Please fill in required fields");
      return;
    }
    
    // FRONTEND GUARD: Block if at limit (defense layer 1)
    // Use backend-provided stats as single source of truth
    if (!stats.can_add_more && !trainer?.is_admin) {
      toast.error(`Client limit reached (${stats.total_clients}/${stats.client_limit}). Go Pro to add more clients.`);
      setShowAddClient(false);
      loadData();
      return;
    }
    
    setAddingClient(true);
    try {
      const payload = {
        name: newClient.name,
        age: parseInt(newClient.age),
        gender: newClient.gender,
        starting_weight: parseFloat(newClient.starting_weight),
        goal: newClient.goal,
        start_date: newClient.start_date,
        phone: newClient.phone || null,
        email: newClient.email || null,
        joining_date: newClient.joining_date || null,
        current_weight: newClient.current_weight ? parseFloat(newClient.current_weight) : null,
        height: newClient.height ? parseFloat(newClient.height) : null,
        target_weight: newClient.target_weight ? parseFloat(newClient.target_weight) : null,
        target_duration_weeks: newClient.target_duration_weeks ? parseInt(newClient.target_duration_weeks) : null,
        plan_duration_weeks: newClient.plan_duration_weeks ? parseInt(newClient.plan_duration_weeks) : null,
        medical_conditions: newClient.medical_conditions || null,
        injuries: newClient.injuries || null,
        coach_notes: newClient.coach_notes || null,
        current_body_fat: newClient.current_body_fat ? parseFloat(newClient.current_body_fat) : null,
        target_body_fat: newClient.target_body_fat ? parseFloat(newClient.target_body_fat) : null,
        activity_level: newClient.activity_level || null,
        client_type: newClient.client_type || null,
        priority: newClient.priority || "normal",
        communication_pref: newClient.communication_pref.length > 0 ? newClient.communication_pref : null,
        plan_price: newClient.plan_price ? parseFloat(newClient.plan_price) : null,
        discount: newClient.discount ? parseFloat(newClient.discount) : null,
        final_amount: computedFinalAmount > 0 ? computedFinalAmount : null,
        payment_status: newClient.payment_status || null,
        payment_mode: newClient.payment_mode || null,
        payment_notes: newClient.payment_notes || null,
      };
      const res = await clientsAPI.create(payload);
      const createdClientId = res.data.id;

      // Upload profile photo if selected
      if (profilePhotoFile) {
        try {
          const formData = new FormData();
          formData.append('file', profilePhotoFile, 'profile.jpg');
          await clientsAPI.uploadPhoto(createdClientId, formData);
        } catch (e) { console.error('Profile photo upload failed:', e); }
      }

      // Upload before photo if selected
      if (beforePhotoFile) {
        try {
          const reader = new FileReader();
          reader.onloadend = async () => {
            await photosAPI.create(createdClientId, {
              date: newClient.start_date,
              photo_data: reader.result,
              photo_type: "before",
              notes: "Before photo"
            });
          };
          reader.readAsDataURL(beforePhotoFile);
        } catch (e) { console.error('Before photo upload failed:', e); }
      }

      toast.success("Client added successfully!");
      setShowAddClient(false);
      setProfilePhotoPreview(null);
      setProfilePhotoFile(null);
      setBeforePhotoPreview(null);
      setBeforePhotoFile(null);
      setNewClient({
        name: "",
        age: "",
        gender: "male",
        starting_weight: "",
        current_weight: "",
        height: "",
        target_weight: "",
        goal: "fat_loss",
        target_duration_weeks: "",
        plan_duration_weeks: "",
        start_date: new Date().toISOString().split('T')[0],
        phone: "",
        email: "",
        joining_date: new Date().toISOString().split('T')[0],
        medical_conditions: "",
        injuries: "",
        coach_notes: "",
        current_body_fat: "",
        target_body_fat: "",
        activity_level: "",
        client_type: "",
        priority: "normal",
        communication_pref: [],
        plan_price: "",
        discount: "",
        payment_status: "",
        payment_mode: "",
        payment_notes: ""
      });
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add client";
      toast.error(message);
      // If limit reached error, refresh data to update UI
      if (error.response?.status === 403) {
        loadData();
        setShowAddClient(false);
      }
    } finally {
      setAddingClient(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      await clientsAPI.delete(deleteConfirm.id);
      toast.success(`${deleteConfirm.name} has been deleted`);
      setDeleteConfirm(null);
      loadData(); // Refresh data to update totals
    } catch (error) {
      toast.error("Failed to delete client");
    } finally {
      setDeleting(false);
    }
  };

  const handleArchiveClient = async () => {
    if (!archiveConfirm) return;
    
    setArchiving(true);
    try {
      await clientsAPI.archive(archiveConfirm.id);
      toast.success(`${archiveConfirm.name} has been archived`);
      setArchiveConfirm(null);
      loadData();
      if (showArchived) loadArchivedClients();
    } catch (error) {
      toast.error("Failed to archive client");
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveClient = async (client) => {
    try {
      await clientsAPI.unarchive(client.id);
      toast.success(`${client.name} has been restored`);
      loadData();
      loadArchivedClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore client");
    }
  };

  const loadArchivedClients = async () => {
    try {
      const res = await clientsAPI.getArchived();
      setArchivedClients(res.data);
    } catch (error) {
      toast.error("Failed to load archived clients");
    }
  };

  const handleSubmitFeedback = async (e) => {
    e.preventDefault();
    if (!feedbackForm.message.trim()) {
      toast.error("Please enter your feedback");
      return;
    }
    
    setSubmittingFeedback(true);
    try {
      await feedbackAPI.submit(feedbackForm);
      toast.success("Thank you for your feedback!");
      setShowFeedback(false);
      setFeedbackForm({ message: "", category: "general" });
    } catch (error) {
      toast.error("Failed to submit feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    if (showArchived) {
      loadArchivedClients();
    }
  }, [showArchived]);

  // Auto-calculated values
  const computedFinalAmount = (() => {
    const price = parseFloat(newClient.plan_price) || 0;
    const disc = parseFloat(newClient.discount) || 0;
    return Math.max(0, price - disc);
  })();

  const autoTargetDuration = (() => {
    const cw = parseFloat(newClient.starting_weight);
    const tw = parseFloat(newClient.target_weight);
    if (!cw || !tw || cw === tw) return null;
    const diff = Math.abs(cw - tw);
    return Math.max(4, Math.round(diff / 0.5));
  })();

  const autoPlanDuration = (() => {
    const dur = parseInt(newClient.target_duration_weeks) || autoTargetDuration;
    if (!dur) return null;
    if (dur <= 6) return "4";
    if (dur <= 10) return "8";
    return "12";
  })();

  const toggleCommPref = (pref) => {
    setNewClient(prev => ({
      ...prev,
      communication_pref: prev.communication_pref.includes(pref)
        ? prev.communication_pref.filter(p => p !== pref)
        : [...prev.communication_pref, pref]
    }));
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getGoalBadgeClass = (goal) => {
    switch (goal) {
      case "fat_loss": return "badge-fat-loss";
      case "muscle_gain": return "badge-muscle-gain";
      case "competition_prep": return "badge-fat-loss";
      case "strength": return "badge-muscle-gain";
      default: return "badge-maintenance";
    }
  };

  const getGoalLabel = (goal) => {
    switch (goal) {
      case "fat_loss": return "Fat Loss";
      case "muscle_gain": return "Muscle Gain";
      default: return "Maintenance";
    }
  };

  // Calculate beta days remaining - fixed end date: 31 May 2026
  const getBetaDaysRemaining = () => {
    const endDate = new Date('2026-05-31T23:59:59Z');
    const now = new Date();
    const diffTime = endDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const betaDaysRemaining = getBetaDaysRemaining();

  // HARD CHECK: Is the trainer at client limit?
  // Use ONLY backend-provided stats as the single source of truth.
  // stats.total_clients counts active + archived on the server.
  // stats.can_add_more is the server's authoritative decision.
  const isAtLimit = !trainer?.is_admin && (
    stats.can_add_more === false || 
    stats.total_clients >= (stats.client_limit || 10)
  );

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (loading || isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(180deg, #0B0F1A 0%, #020617 100%)'}}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24 md:pb-8 noise-overlay" style={{background: 'linear-gradient(180deg, #0B0F1A 0%, #020617 100%)'}}>
      {/* Header */}
      <Header subtitle={trainer?.name} />

      {/* Early Coach Plan Info Banner */}
      <div className="glass border-b border-primary/10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                <Crown className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-sm font-bold text-primary uppercase tracking-wide">
                  Early Coach Plan
                </h3>
                <p className="text-xs text-muted-foreground">
                  Free for early coaches (limited slots)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-white/[0.03] rounded-xl px-3 py-2 border border-white/[0.05]">
              <Gift className="w-4 h-4 text-primary" />
              <span>10 clients free forever &middot; <span className="text-primary font-bold">Only first 50 coaches get lifetime discount</span></span>
            </div>
          </div>
        </div>
      </div>

      <main className="container mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="glass-card border-0" data-testid="total-clients-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">
                    Total Clients
                  </p>
                  <p className="stat-number text-foreground">{stats.total_clients}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats.total_clients} of {stats.client_limit || 10} limit
                  </p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Users className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0" data-testid="active-clients-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">
                    Active
                  </p>
                  <p className="stat-number text-neon-green">{stats.active_clients}</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-neon-green/10 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-neon-green" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-card border-0" data-testid="needs-update-card">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">
                    Needs Update
                  </p>
                  <p className="stat-number text-destructive">{stats.clients_needing_update}</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-destructive/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-destructive" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="glass-card border-0 cursor-pointer hover:bg-secondary/50 transition-colors" 
            onClick={() => setShowArchived(!showArchived)}
            data-testid="archived-clients-card"
          >
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-bold tracking-widest uppercase text-muted-foreground mb-2">
                    Archived
                  </p>
                  <p className="stat-number text-muted-foreground">{stats.archived_clients}</p>
                </div>
                <div className="w-11 h-11 rounded-xl bg-secondary/50 flex items-center justify-center">
                  <Archive className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Quick Access */}
        <div className="flex items-center gap-3" data-testid="analytics-quick-access">
          <Button
            onClick={() => navigate("/analytics")}
            variant="outline"
            className="border-primary/30 text-primary hover:bg-primary/10 font-bold"
            data-testid="view-analytics-btn"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analytics
          </Button>
          <Button
            onClick={() => navigate("/invite")}
            variant="outline"
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 font-bold"
            data-testid="invite-earn-btn"
          >
            <Gift className="w-4 h-4 mr-2" />
            Invite & Earn
          </Button>
        </div>

        {/* Add Your First Client - Hero CTA for new users */}
        {clients.length === 0 && !loading && (
          <Card className="glass-card border-0 border-l-4 border-l-primary overflow-hidden" data-testid="first-client-cta">
            <CardContent className="p-8 text-center relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <UserCheck className="w-10 h-10 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-extrabold tracking-tight uppercase text-foreground mb-2" data-testid="first-client-heading">
                Add Your First Client
              </h3>
              <p className="text-muted-foreground mb-6 font-medium">
                Get started in <span className="text-primary font-bold">30 seconds</span> — add a client and start tracking their progress
              </p>
              <Button
                onClick={() => setShowAddClient(true)}
                className="h-14 px-10 btn-premium text-white font-extrabold tracking-wide uppercase rounded-xl text-lg"
                data-testid="first-client-add-btn"
              >
                <Plus className="w-6 h-6 mr-2" />
                Add Your First Client
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Welcome Popup - After first signup */}
        <Dialog open={showWelcomePopup} onOpenChange={setShowWelcomePopup}>
          <DialogContent className="bg-card border-border max-w-sm text-center">
            <DialogHeader>
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-primary" />
              </div>
              <DialogTitle className="font-heading text-2xl font-extrabold uppercase tracking-tight">
                Welcome to FitTrack!
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-base font-medium mt-2">
                Add your first client now <span className="text-primary font-bold">(takes 30 sec)</span>
              </DialogDescription>
            </DialogHeader>
            <Button
              onClick={() => { setShowWelcomePopup(false); setShowAddClient(true); }}
              className="w-full h-14 btn-premium text-white font-extrabold tracking-wide uppercase rounded-xl text-lg mt-2"
              data-testid="welcome-add-client-btn"
            >
              <Plus className="w-6 h-6 mr-2" />
              Add Your First Client
            </Button>
            <Button
              onClick={() => setShowWelcomePopup(false)}
              variant="ghost"
              className="w-full text-muted-foreground hover:text-foreground font-medium"
              data-testid="welcome-skip-btn"
            >
              I'll do it later
            </Button>
          </DialogContent>
        </Dialog>

        {/* Client Limit Warning */}
        {isAtLimit && (
          <Card className="border-l-4 border-l-primary bg-primary/5" data-testid="client-limit-warning">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <AlertCircle className="w-5 h-5 text-primary shrink-0" />
                  <div className="min-w-0">
                    <p className="font-extrabold text-foreground text-sm">You've hit your current limit — time to grow</p>
                    <p className="text-xs text-muted-foreground">
                      Upgrade & manage unlimited clients without limits
                    </p>
                  </div>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => navigate("/pricing")}
                  className="btn-premium text-white font-bold whitespace-nowrap w-full sm:w-auto shrink-0"
                  data-testid="upgrade-btn"
                >
                  Go Pro
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reminders Section — Smart Needs Attention */}
        {reminders.length > 0 && (
          <Card className="glass-card border-0 border-l-4 border-l-amber-500" data-testid="reminders-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg tracking-wide uppercase flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  Needs Attention
                  <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-400 ml-1">{reminders.length}</Badge>
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {reminders.slice(0, 5).map((reminder) => (
                <div
                  key={reminder.client_id}
                  className="flex items-center justify-between p-3 bg-white/[0.03] rounded-xl cursor-pointer hover:bg-amber-500/5 transition-colors"
                  onClick={() => navigate(`/clients/${reminder.client_id}`)}
                  data-testid={`reminder-${reminder.client_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <AlertCircle className="w-4 h-4 text-amber-500" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{reminder.client_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {reminder.days_since_update > 100 ? "No progress updates yet" : `${reminder.days_since_update} days since last update`}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              ))}
              {reminders.length > 5 && (
                <p className="text-xs text-center text-muted-foreground pt-1">+{reminders.length - 5} more clients need attention</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Today's Attendance — Batch Marking */}
        {todayAttendance && todayAttendance.clients && todayAttendance.clients.length > 0 && (
          <Card className="glass-card border-0" data-testid="batch-attendance-section">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg tracking-wide uppercase flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-primary" />
                  Today's Attendance
                  <span className="text-xs font-normal text-muted-foreground normal-case ml-1">
                    {todayAttendance.date}
                  </span>
                </CardTitle>
              </div>
              <div className="flex gap-2 mt-2">
                <Button
                  size="sm" variant="outline"
                  disabled={batchMarking}
                  className="border-green-500/30 text-green-400 hover:bg-green-500/10 text-xs font-bold"
                  data-testid="batch-mark-present"
                  onClick={async () => {
                    setBatchMarking(true);
                    try {
                      await attendanceAPI.batchMark("present");
                      const res = await attendanceAPI.getToday();
                      setTodayAttendance(res.data);
                      toast.success("All clients marked present");
                    } catch { toast.error("Failed to mark attendance"); }
                    finally { setBatchMarking(false); }
                  }}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                  All Present
                </Button>
                <Button
                  size="sm" variant="outline"
                  disabled={batchMarking}
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs font-bold"
                  data-testid="batch-mark-absent"
                  onClick={async () => {
                    setBatchMarking(true);
                    try {
                      await attendanceAPI.batchMark("absent");
                      const res = await attendanceAPI.getToday();
                      setTodayAttendance(res.data);
                      toast.success("All clients marked absent");
                    } catch { toast.error("Failed to mark attendance"); }
                    finally { setBatchMarking(false); }
                  }}
                >
                  <XCircle className="w-3.5 h-3.5 mr-1" />
                  All Absent
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5">
              {todayAttendance.clients.map((c) => (
                <div key={c.client_id} className="flex items-center justify-between p-3 bg-white/[0.03] rounded-xl" data-testid={`attendance-row-${c.client_id}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${c.status === 'present' ? 'bg-green-400' : c.status === 'absent' ? 'bg-red-400' : c.status === 'late' ? 'bg-amber-400' : 'bg-zinc-600'}`} />
                    <span className="text-sm font-medium text-foreground truncate">{c.name}</span>
                  </div>
                  <div className="flex gap-1">
                    {["present", "late", "absent"].map((s) => (
                      <button
                        key={s}
                        disabled={batchMarking}
                        className={`px-2.5 py-1 text-[10px] font-bold uppercase rounded-md transition-colors ${
                          c.status === s
                            ? s === 'present' ? 'bg-green-500/20 text-green-400' : s === 'late' ? 'bg-amber-500/20 text-amber-400' : 'bg-red-500/20 text-red-400'
                            : 'bg-white/[0.03] text-muted-foreground hover:bg-white/[0.06]'
                        }`}
                        data-testid={`attendance-${c.client_id}-${s}`}
                        onClick={async () => {
                          try {
                            await attendanceAPI.mark(c.client_id, { status: s });
                            const res = await attendanceAPI.getToday();
                            setTodayAttendance(res.data);
                          } catch { toast.error("Failed"); }
                        }}
                      >
                        {s === 'present' ? 'P' : s === 'late' ? 'L' : 'A'}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Revenue Dashboard */}
        {revenueData && (
          <Card className="glass-card border-0" data-testid="revenue-dashboard-section">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-lg tracking-wide uppercase flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-400" />
                Revenue Overview
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-emerald-500/5 rounded-xl">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">This Month</p>
                  <p className="text-lg font-extrabold text-emerald-400 mt-1">
                    {revenueData.totals.this_month_collected > 0 ? `₹${revenueData.totals.this_month_collected.toLocaleString('en-IN')}` : '₹0'}
                  </p>
                </div>
                <div className="text-center p-3 bg-blue-500/5 rounded-xl">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Expected</p>
                  <p className="text-lg font-extrabold text-blue-400 mt-1">
                    {revenueData.totals.total_expected_monthly > 0 ? `₹${revenueData.totals.total_expected_monthly.toLocaleString('en-IN')}` : '₹0'}
                  </p>
                </div>
                <div className="text-center p-3 bg-amber-500/5 rounded-xl">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Pending</p>
                  <p className="text-lg font-extrabold text-amber-400 mt-1">
                    {revenueData.totals.pending_clients}
                  </p>
                </div>
              </div>

              {/* Monthly Chart (simple bar visualization) */}
              {revenueData.monthly_chart && revenueData.monthly_chart.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-3">Last 6 Months</p>
                  <div className="flex items-end gap-2 h-24">
                    {(() => {
                      const maxVal = Math.max(...revenueData.monthly_chart.map(m => m.collected), 1);
                      return revenueData.monthly_chart.map((m, i) => (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-[9px] font-bold text-emerald-400">
                            {m.collected > 0 ? `₹${m.collected >= 1000 ? (m.collected / 1000).toFixed(1) + 'k' : m.collected}` : ''}
                          </span>
                          <div
                            className="w-full rounded-t-md bg-emerald-500/20 hover:bg-emerald-500/30 transition-colors relative"
                            style={{ height: `${Math.max((m.collected / maxVal) * 64, 4)}px` }}
                            data-testid={`revenue-bar-${m.month}`}
                          >
                            {m.collected > 0 && <div className="absolute inset-x-0 bottom-0 rounded-t-md bg-emerald-500/40" style={{ height: '100%' }} />}
                          </div>
                          <span className="text-[10px] text-muted-foreground font-medium">{m.month}</span>
                        </div>
                      ));
                    })()}
                  </div>
                </div>
              )}

              {/* Client-wise Breakdown */}
              {revenueData.client_breakdown && revenueData.client_breakdown.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Client Payments</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {revenueData.client_breakdown.map((c) => (
                      <div key={c.client_id}
                        className="flex items-center justify-between p-2.5 bg-white/[0.03] rounded-lg cursor-pointer hover:bg-white/[0.06] transition-colors"
                        onClick={() => navigate(`/clients/${c.client_id}`)}
                        data-testid={`revenue-client-${c.client_id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`w-2 h-2 rounded-full shrink-0 ${c.payment_status === 'paid' ? 'bg-emerald-400' : c.payment_status === 'overdue' ? 'bg-red-400' : 'bg-amber-400'}`} />
                          <span className="text-sm font-medium text-foreground truncate">{c.name}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-xs text-muted-foreground">
                            {c.plan_price > 0 ? `₹${c.plan_price.toLocaleString('en-IN')}/mo` : '—'}
                          </span>
                          <span className={`text-xs font-bold ${c.total_paid > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                            ₹{c.total_paid.toLocaleString('en-IN')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 6-month total */}
              <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground">6-Month Total</span>
                <span className="text-base font-extrabold text-emerald-400">
                  ₹{(revenueData.totals.total_collected_6m || 0).toLocaleString('en-IN')}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pro Features Card */}
        <Card className="glass-card border-0 border-primary/20 overflow-hidden" data-testid="pro-features-card">
          <CardContent className="p-5 relative">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-cyan-400 to-blue-500" />
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-heading text-base font-extrabold uppercase tracking-tight text-foreground">
                  Go Pro & Remove Limits
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Manage all your clients in one place — <span className="font-bold text-primary">save hours every week</span>
                </p>
              </div>
              <Button
                onClick={() => navigate("/pricing")}
                size="sm"
                className="btn-premium text-white font-bold px-4 shrink-0"
                data-testid="pro-upgrade-btn"
              >
                View Plans
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {["50+ Clients", "PDF Reports", "Diet Plans", "Before/After Gallery", "AI Suggestions"].map((f) => (
                <span key={f} className="text-[10px] font-semibold px-2 py-1 bg-primary/10 text-primary rounded-full">
                  {f}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Search and Add */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search clients..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 bg-white/[0.03] border-white/[0.06] focus:border-brand-blue/50 rounded-xl transition-smooth"
              data-testid="search-clients-input"
            />
          </div>
          
          {/* Add Client - Disabled when limit reached */}
          {isAtLimit ? (
            <Button
              className="h-12 px-6 bg-white/[0.05] text-muted-foreground font-semibold tracking-wide uppercase rounded-xl cursor-not-allowed opacity-60"
              disabled
              data-testid="add-client-btn-disabled"
            >
              <Plus className="w-5 h-5 mr-2" />
              <span className="hidden sm:inline">Add Client</span>
            </Button>
          ) : (
          <Dialog open={showAddClient} onOpenChange={(open) => {
            // Block opening dialog if at limit (use backend stats)
            if (open && isAtLimit) {
              toast.error(`Client limit reached (${stats.total_clients}/${stats.client_limit}). Go Pro to add more.`);
              return;
            }
            setShowAddClient(open);
          }}>
            <DialogTrigger asChild>
              <Button
                className="h-12 px-4 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
                data-testid="add-client-btn"
              >
                <Plus className="w-5 h-5 mr-1" />
                Add
              </Button>
            </DialogTrigger>

          {/* CSV Import Button (Pro/Elite) */}
          {(trainer?.plan_type === "pro" || trainer?.plan_type === "elite" || trainer?.is_admin) && (
            <Button
              variant="outline"
              className="h-12 px-4 border-primary/30 text-primary hover:bg-primary/10 font-semibold tracking-wide uppercase rounded-xl"
              onClick={() => setShowCsvImport(true)}
              data-testid="csv-import-btn"
            >
              <Upload className="w-5 h-5 mr-1" />
              <span className="hidden sm:inline">CSV</span>
            </Button>
          )}
            <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto pt-8 sm:pt-6">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl tracking-tight uppercase">
                  Add New Client
                </DialogTitle>
                <DialogDescription className="text-muted-foreground text-sm">
                  Fill in client details to start their coaching journey.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddClient} className="space-y-5 mt-2">

                {/* ── Photo Upload ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-border/20">
                    <Camera className="w-4 h-4 text-[#00BFFF]" />
                    <span className="text-xs font-bold tracking-widest uppercase text-[#00BFFF]">Photos</span>
                    <Badge variant="outline" className="text-[10px] border-white/10 text-muted-foreground ml-auto">Optional</Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Profile Photo</Label>
                      <div onClick={() => profilePhotoRef.current?.click()} className="relative group cursor-pointer rounded-xl border-2 border-dashed border-white/10 hover:border-[#00BFFF]/30 transition-smooth overflow-hidden aspect-square flex items-center justify-center bg-white/[0.02]" data-testid="profile-photo-upload">
                        {profilePhotoPreview ? (
                          <>
                            <img src={profilePhotoPreview} alt="Profile" className="w-full h-full object-cover" />
                            <button type="button" onClick={(e) => { e.stopPropagation(); setProfilePhotoPreview(null); setProfilePhotoFile(null); }} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-red-500/80 transition-smooth" data-testid="remove-profile-photo"><X className="w-3 h-3" /></button>
                          </>
                        ) : (
                          <div className="text-center p-3"><Upload className="w-6 h-6 text-muted-foreground mx-auto mb-1 group-hover:text-[#00BFFF] transition-smooth" /><p className="text-[10px] text-muted-foreground">Upload</p></div>
                        )}
                      </div>
                      <input ref={profilePhotoRef} type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 5*1024*1024) { toast.error("Max 5MB"); return; } setProfilePhotoFile(file); setProfilePhotoPreview(URL.createObjectURL(file)); e.target.value=''; }} data-testid="profile-photo-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Before Photo</Label>
                      <div onClick={() => beforePhotoRef.current?.click()} className="relative group cursor-pointer rounded-xl border-2 border-dashed border-white/10 hover:border-[#00BFFF]/30 transition-smooth overflow-hidden aspect-square flex items-center justify-center bg-white/[0.02]" data-testid="before-photo-upload">
                        {beforePhotoPreview ? (
                          <>
                            <img src={beforePhotoPreview} alt="Before" className="w-full h-full object-cover" />
                            <button type="button" onClick={(e) => { e.stopPropagation(); setBeforePhotoPreview(null); setBeforePhotoFile(null); }} className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center text-white hover:bg-red-500/80 transition-smooth" data-testid="remove-before-photo"><X className="w-3 h-3" /></button>
                          </>
                        ) : (
                          <div className="text-center p-3"><Image className="w-6 h-6 text-muted-foreground mx-auto mb-1 group-hover:text-[#00BFFF] transition-smooth" /><p className="text-[10px] text-muted-foreground">Before</p></div>
                        )}
                      </div>
                      <input ref={beforePhotoRef} type="file" accept="image/jpeg,image/png" capture="environment" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 5*1024*1024) { toast.error("Max 5MB"); return; } setBeforePhotoFile(file); setBeforePhotoPreview(URL.createObjectURL(file)); e.target.value=''; }} data-testid="before-photo-input" />
                    </div>
                  </div>
                </div>

                {/* ── Basic Info ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-border/20">
                    <Users className="w-4 h-4 text-[#00BFFF]" />
                    <span className="text-xs font-bold tracking-widest uppercase text-[#00BFFF]">Basic Info</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2 col-span-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Name <span className="text-red-400">*</span></Label>
                      <Input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} placeholder="Client name" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Age <span className="text-red-400">*</span></Label>
                      <Input type="number" value={newClient.age} onChange={(e) => setNewClient({ ...newClient, age: e.target.value })} placeholder="28" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-age" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Gender</Label>
                      <Select value={newClient.gender} onValueChange={(v) => setNewClient({ ...newClient, gender: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-gender"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Height (cm)</Label>
                      <Input type="number" step="0.1" value={newClient.height} onChange={(e) => setNewClient({ ...newClient, height: e.target.value })} placeholder="175" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-height" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Priority</Label>
                      <Select value={newClient.priority} onValueChange={(v) => setNewClient({ ...newClient, priority: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-priority"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="priority">Priority</SelectItem>
                          <SelectItem value="vip">VIP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Client Type</Label>
                      <Select value={newClient.client_type} onValueChange={(v) => setNewClient({ ...newClient, client_type: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-type"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="online">Online Coaching</SelectItem>
                          <SelectItem value="offline">Offline Training</SelectItem>
                          <SelectItem value="personal">Personal Training</SelectItem>
                          <SelectItem value="diet_only">Diet Only</SelectItem>
                          <SelectItem value="custom">Custom</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Phone</Label>
                      <Input value={newClient.phone} onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })} placeholder="+91 98765 43210" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-phone" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Email</Label>
                      <Input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} placeholder="client@email.com" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-email" />
                    </div>
                  </div>
                  {/* Communication Preference */}
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Contact via</Label>
                    <div className="flex gap-2">
                      {[
                        { value: "whatsapp", label: "WhatsApp", Icon: MessageSquare },
                        { value: "call", label: "Call", Icon: Phone },
                        { value: "email", label: "Email", Icon: Mail }
                      ].map(opt => (
                        <button key={opt.value} type="button" onClick={() => toggleCommPref(opt.value)}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-smooth ${newClient.communication_pref.includes(opt.value) ? 'bg-[#00BFFF]/15 text-[#00BFFF] border border-[#00BFFF]/30' : 'bg-white/[0.02] text-muted-foreground border border-white/[0.04] hover:border-[#00BFFF]/15'}`}
                          data-testid={`comm-${opt.value}`}>
                          <opt.Icon className="w-3.5 h-3.5" />{opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ── Goals & Metrics ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-border/20">
                    <Target className="w-4 h-4 text-[#00BFFF]" />
                    <span className="text-xs font-bold tracking-widest uppercase text-[#00BFFF]">Goals & Metrics</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Current Wt (kg) <span className="text-red-400">*</span></Label>
                      <Input type="number" step="0.1" value={newClient.starting_weight} onChange={(e) => setNewClient({ ...newClient, starting_weight: e.target.value, current_weight: e.target.value })} placeholder="85" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-weight" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Target Wt (kg)</Label>
                      <Input type="number" step="0.1" value={newClient.target_weight} onChange={(e) => setNewClient({ ...newClient, target_weight: e.target.value })} placeholder="72" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-target-weight" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Goal</Label>
                      <Select value={newClient.goal} onValueChange={(v) => setNewClient({ ...newClient, goal: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-goal"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fat_loss">Fat Loss</SelectItem>
                          <SelectItem value="muscle_gain">Muscle Gain</SelectItem>
                          <SelectItem value="recomp">Recomposition</SelectItem>
                          <SelectItem value="strength">Strength</SelectItem>
                          <SelectItem value="general_fitness">General Fitness</SelectItem>
                          <SelectItem value="competition_prep">Competition Prep</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Activity Level</Label>
                      <Select value={newClient.activity_level} onValueChange={(v) => setNewClient({ ...newClient, activity_level: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-activity"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sedentary">Sedentary</SelectItem>
                          <SelectItem value="light_active">Light Active</SelectItem>
                          <SelectItem value="moderately_active">Moderately Active</SelectItem>
                          <SelectItem value="very_active">Very Active</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Body Fat %</Label>
                      <Input type="number" step="0.1" value={newClient.current_body_fat} onChange={(e) => setNewClient({ ...newClient, current_body_fat: e.target.value })} placeholder="22" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-body-fat" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Target BF %</Label>
                      <Input type="number" step="0.1" value={newClient.target_body_fat} onChange={(e) => setNewClient({ ...newClient, target_body_fat: e.target.value })} placeholder="15" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-target-body-fat" />
                    </div>
                  </div>
                </div>

                {/* ── Plan Setup ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-border/20">
                    <ClipboardList className="w-4 h-4 text-[#00BFFF]" />
                    <span className="text-xs font-bold tracking-widest uppercase text-[#00BFFF]">Plan Setup</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Target Duration (wks)</Label>
                      <div className="relative">
                        <Input type="number" value={newClient.target_duration_weeks} onChange={(e) => setNewClient({ ...newClient, target_duration_weeks: e.target.value })} placeholder={autoTargetDuration ? `~${autoTargetDuration}` : "16"} className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-target-duration" />
                        {autoTargetDuration && !newClient.target_duration_weeks && (
                          <button type="button" onClick={() => setNewClient({ ...newClient, target_duration_weeks: String(autoTargetDuration) })} className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#00BFFF] hover:underline" data-testid="auto-target-duration">Use ~{autoTargetDuration}w</button>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Plan Duration</Label>
                      <Select value={newClient.plan_duration_weeks || (autoPlanDuration && !newClient.plan_duration_weeks ? "" : "")} onValueChange={(v) => setNewClient({ ...newClient, plan_duration_weeks: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-plan-duration">
                          <SelectValue placeholder={autoPlanDuration ? `Suggested: ${autoPlanDuration}w` : "Select"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="4">4 Weeks</SelectItem>
                          <SelectItem value="8">8 Weeks</SelectItem>
                          <SelectItem value="12">12 Weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Start Date</Label>
                      <Input type="date" value={newClient.start_date} onChange={(e) => setNewClient({ ...newClient, start_date: e.target.value })} className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-start-date" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Joining Date</Label>
                      <Input type="date" value={newClient.joining_date} onChange={(e) => setNewClient({ ...newClient, joining_date: e.target.value })} className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-joining-date" />
                    </div>
                  </div>
                </div>

                {/* ── Payment ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-border/20">
                    <CreditCard className="w-4 h-4 text-[#00BFFF]" />
                    <span className="text-xs font-bold tracking-widest uppercase text-[#00BFFF]">Payment</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Plan Price</Label>
                      <Input type="number" step="1" value={newClient.plan_price} onChange={(e) => setNewClient({ ...newClient, plan_price: e.target.value })} placeholder="5000" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-plan-price" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Discount</Label>
                      <Input type="number" step="1" value={newClient.discount} onChange={(e) => setNewClient({ ...newClient, discount: e.target.value })} placeholder="500" className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-discount" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Final Amount</Label>
                      <div className="h-11 bg-secondary/30 border border-white/[0.06] rounded-md flex items-center px-3 text-sm font-semibold text-[#00BFFF]" data-testid="new-client-final-amount">
                        {computedFinalAmount > 0 ? `₹${computedFinalAmount.toLocaleString()}` : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Payment Status</Label>
                      <Select value={newClient.payment_status} onValueChange={(v) => setNewClient({ ...newClient, payment_status: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-payment-status"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Payment Mode</Label>
                      <Select value={newClient.payment_mode} onValueChange={(v) => setNewClient({ ...newClient, payment_mode: v })}>
                        <SelectTrigger className="h-11 bg-secondary/50 border-transparent" data-testid="new-client-payment-mode"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="upi">UPI</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="bank">Bank Transfer</SelectItem>
                          <SelectItem value="card">Card</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Payment Notes</Label>
                    <Input value={newClient.payment_notes} onChange={(e) => setNewClient({ ...newClient, payment_notes: e.target.value })} placeholder="e.g., EMI 2 of 3, paid via GPay..." className="h-11 bg-secondary/50 border-transparent text-sm" data-testid="new-client-payment-notes" />
                  </div>
                </div>

                {/* ── Health Info ── */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 pb-1 border-b border-border/20">
                    <Heart className="w-4 h-4 text-[#00BFFF]" />
                    <span className="text-xs font-bold tracking-widest uppercase text-[#00BFFF]">Health Info</span>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Medical Conditions</Label>
                    <Textarea value={newClient.medical_conditions} onChange={(e) => setNewClient({ ...newClient, medical_conditions: e.target.value })} placeholder="e.g., Diabetes, Hypertension, Asthma..." className="bg-secondary/50 border-transparent min-h-[60px] text-sm" data-testid="new-client-medical" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">Injuries</Label>
                    <Textarea value={newClient.injuries} onChange={(e) => setNewClient({ ...newClient, injuries: e.target.value })} placeholder="e.g., Lower back pain, Knee surgery (2024)..." className="bg-secondary/50 border-transparent min-h-[60px] text-sm" data-testid="new-client-injuries" />
                  </div>
                </div>

                <Button type="submit" className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl" disabled={addingClient} data-testid="submit-new-client">
                  {addingClient ? <div className="spinner" /> : "Add Client"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>

        {/* CSV Import Dialog */}
        <Dialog open={showCsvImport} onOpenChange={setShowCsvImport}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg uppercase">Import Clients from CSV</DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">Upload a CSV file to add multiple clients at once</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div
                className="border-2 border-dashed border-white/10 rounded-xl p-6 text-center cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => csvFileRef.current?.click()}
                data-testid="csv-dropzone"
              >
                <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-foreground font-semibold">Click to upload CSV</p>
                <p className="text-xs text-muted-foreground mt-1">name, age, gender, weight, goal, phone, email</p>
                <input ref={csvFileRef} type="file" accept=".csv" className="hidden" data-testid="csv-file-input"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setCsvUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append("file", file);
                      const res = await csvImportAPI.importFile(formData);
                      toast.success(res.data.message);
                      setShowCsvImport(false);
                      // Refresh clients
                      const clientsRes = await clientsAPI.getAll();
                      setClients(clientsRes.data);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "CSV import failed");
                    } finally {
                      setCsvUploading(false);
                      e.target.value = "";
                    }
                  }} />
              </div>
              {csvUploading && (
                <div className="flex items-center justify-center gap-2 text-sm text-primary">
                  <div className="spinner" /> Importing...
                </div>
              )}
              <Button variant="outline" className="w-full h-10 text-sm border-white/10" data-testid="csv-template-btn"
                onClick={async () => {
                  try {
                    const res = await csvImportAPI.downloadTemplate();
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a");
                    a.href = url; a.download = "fittrack_import_template.csv"; a.click();
                    window.URL.revokeObjectURL(url);
                    toast.success("Template downloaded!");
                  } catch { toast.error("Download failed"); }
                }}>
                Download Sample Template
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Client List */}
        <div className="space-y-3">
          <h2 className="font-heading text-lg tracking-wide uppercase text-muted-foreground">
            Your Clients
          </h2>
          
          {filteredClients.length === 0 ? (
            <Card className="empty-state rounded-2xl">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-2xl bg-secondary/50 flex items-center justify-center mx-auto mb-6">
                  <Users className="w-10 h-10 empty-state-icon" />
                </div>
                <h3 className="font-heading text-xl font-bold tracking-tight uppercase text-foreground mb-2">
                  {searchQuery ? "No Results Found" : "No Clients Yet"}
                </h3>
                <p className="text-muted-foreground max-w-sm mx-auto">
                  {searchQuery 
                    ? "Try adjusting your search terms or clearing the filter" 
                    : "Start building your client roster. Add your first client to begin tracking their fitness journey."}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClients.map((client) => (
                <Card
                  key={client.id}
                  className="glass-card border-0 hover:scale-[1.01] transition-transform duration-200"
                  data-testid={`client-card-${client.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div 
                        className="flex items-center gap-3 flex-1 cursor-pointer"
                        onClick={() => navigate(`/clients/${client.id}`)}
                      >
                        {/* Client Profile Photo */}
                        <Avatar
                          src={client.profile_photo}
                          name={client.name}
                          size="md"
                          testId={`client-avatar-${client.id}`}
                        />
                        <div>
                          <h3 className="font-heading text-xl font-bold tracking-tight text-foreground">
                            {client.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {client.age} yrs, {client.gender}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${getGoalBadgeClass(client.goal)} text-xs font-semibold px-3 py-1 rounded-full`}>
                          {getGoalLabel(client.goal)}
                        </Badge>
                      </div>
                    </div>
                    
                    <div 
                      className="grid grid-cols-3 gap-2 text-center cursor-pointer"
                      onClick={() => navigate(`/clients/${client.id}`)}
                    >
                      <div className="p-3 bg-secondary/40 rounded-xl">
                        <Scale className="w-4 h-4 text-primary mx-auto mb-1" />
                        <p className="font-heading text-xl font-bold text-foreground">
                          {calculateWeightProgress(client).currentWeight}
                        </p>
                        <p className="text-xs text-muted-foreground">kg</p>
                      </div>
                      <div className="p-3 bg-secondary/40 rounded-xl">
                        <Target className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                        <p className="font-heading text-xl font-bold text-foreground">
                          {calculateWeightProgress(client).startWeight}
                        </p>
                        <p className="text-xs text-muted-foreground">start</p>
                      </div>
                      <div className="p-3 bg-secondary/40 rounded-xl">
                        <Calendar className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                        <p className="font-heading text-xl font-bold text-foreground">
                          {Math.floor((new Date() - new Date(client.start_date)) / (1000 * 60 * 60 * 24 * 7))}
                        </p>
                        <p className="text-xs text-muted-foreground">weeks</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border/30">
                      <span className={`text-xs font-medium ${client.is_active ? 'text-neon-green' : 'text-muted-foreground'}`}>
                        {client.is_active ? '● Active' : '○ Inactive'}
                      </span>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/clients/${client.id}`);
                          }}
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-amber-500 hover:bg-amber-500/10 rounded-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchiveConfirm(client);
                          }}
                          data-testid={`archive-client-${client.id}`}
                          title="Archive client"
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirm(client);
                          }}
                          data-testid={`delete-client-${client.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Plan & Upgrade Section */}
        <div className="space-y-4" data-testid="dashboard-pricing-section">
          <Card className="glass-card border-0 ring-1 ring-primary/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading text-lg font-extrabold text-foreground uppercase" data-testid="dashboard-beta-title">Early Coach Plan</h3>
                    <Badge className="bg-primary/10 text-primary border-primary/30">Active</Badge>
                  </div>
                </div>
              </div>
              
              <div className="mb-4">
                <h4 className="font-heading text-base font-extrabold text-foreground uppercase tracking-wide" data-testid="dashboard-beta-trial-title">
                  Free for Early Coaches
                </h4>
                <p className="text-sm text-muted-foreground mt-1 font-medium" data-testid="dashboard-beta-trial-desc">
                  10 clients free forever + <span className="text-primary font-bold">lifetime discount on Pro</span>
                </p>
              </div>

              <ul className="space-y-2 mb-4">
                {[
                  "Track every client detail easily",
                  "Show client transformation (before/after)",
                  "See progress clearly with charts",
                  "Share results on Instagram instantly",
                  "Manage clients in one place — save hours",
                ].map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm">
                    <div className="w-4 h-4 rounded-full flex items-center justify-center bg-neon-green/10 text-neon-green flex-shrink-0">
                      <Check className="w-2.5 h-2.5" />
                    </div>
                    <span className="text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="glass-card border-0 border-primary/20">
            <CardContent className="p-6 text-center">
              <Sparkles className="w-6 h-6 text-primary mx-auto mb-3" />
              <h4 className="font-heading text-base font-extrabold text-foreground uppercase mb-1" data-testid="dashboard-pro-coming-soon">
                Only First 50 Coaches Get Lifetime Discount
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Go Pro to handle 50+ clients, PDF reports, diet plans & AI suggestions
              </p>
              <Button onClick={() => navigate("/pricing")} className="btn-premium text-white font-bold" data-testid="dashboard-go-pro-btn">
                Go Pro & Remove Limits
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Floating Feedback Button */}
      <button
        onClick={() => setShowFeedback(true)}
        className="fixed bottom-5 right-5 z-[9999] h-14 w-14 rounded-full flex items-center justify-center shadow-[0_0_20px_rgba(0,191,255,0.4)] hover:shadow-[0_0_30px_rgba(0,191,255,0.6)] transition-all duration-300 hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #00BFFF, #0090CC)' }}
        data-testid="floating-feedback-btn"
        title="Send Feedback"
      >
        <MessageSquare className="w-5 h-5 text-white" />
      </button>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight uppercase text-destructive">
              Delete Client
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to delete <span className="font-semibold text-foreground">{deleteConfirm?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-destructive/10 rounded-xl border border-destructive/20 my-4">
            <p className="text-sm text-destructive">
              This action cannot be undone. All progress data, workouts, photos, and notes for this client will be permanently deleted.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              className="rounded-lg"
              data-testid="cancel-delete"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteClient}
              disabled={deleting}
              className="bg-destructive hover:bg-destructive/90 text-white rounded-lg"
              data-testid="confirm-delete"
            >
              {deleting ? <div className="spinner" /> : "Delete Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={!!archiveConfirm} onOpenChange={() => setArchiveConfirm(null)}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight uppercase text-amber-500">
              Archive Client
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Are you sure you want to archive <span className="font-semibold text-foreground">{archiveConfirm?.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20 my-4">
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Archived clients are hidden from your main list but can be restored anytime. Their data will be preserved.
            </p>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setArchiveConfirm(null)}
              className="rounded-lg"
              data-testid="cancel-archive"
            >
              Cancel
            </Button>
            <Button
              onClick={handleArchiveClient}
              disabled={archiving}
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-lg"
              data-testid="confirm-archive"
            >
              {archiving ? <div className="spinner" /> : "Archive Client"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archived Clients Modal */}
      <Dialog open={showArchived} onOpenChange={setShowArchived}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight uppercase flex items-center gap-2">
              <Archive className="w-5 h-5 text-muted-foreground" />
              Archived Clients
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            {archivedClients.length === 0 ? (
              <div className="text-center py-8">
                <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No archived clients</p>
              </div>
            ) : (
              archivedClients.map((client) => (
                <div
                  key={client.id}
                  className="flex items-center justify-between p-4 bg-white/[0.03] rounded-xl"
                  data-testid={`archived-client-${client.id}`}
                >
                  <div>
                    <p className="font-semibold text-foreground">{client.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {client.age} yrs • {getGoalLabel(client.goal)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleUnarchiveClient(client)}
                    className="bg-primary hover:bg-primary/90 text-white"
                    data-testid={`restore-client-${client.id}`}
                  >
                    Restore
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal */}
      <Dialog open={showFeedback} onOpenChange={setShowFeedback}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl tracking-tight uppercase flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Send Feedback
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              We'd love to hear your thoughts! Your feedback helps us improve FitTrack.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitFeedback} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                Category
              </Label>
              <Select
                value={feedbackForm.category}
                onValueChange={(value) => setFeedbackForm({ ...feedbackForm, category: value })}
              >
                <SelectTrigger className="h-12 bg-secondary/50 border-transparent" data-testid="feedback-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Feedback</SelectItem>
                  <SelectItem value="feature">Feature Request</SelectItem>
                  <SelectItem value="bug">Bug Report</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-bold tracking-widest uppercase text-muted-foreground">
                Your Message *
              </Label>
              <Textarea
                value={feedbackForm.message}
                onChange={(e) => setFeedbackForm({ ...feedbackForm, message: e.target.value })}
                placeholder="Tell us what you think..."
                className="bg-secondary/50 border-transparent min-h-[120px]"
                data-testid="feedback-message"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full h-12 btn-premium text-white font-semibold tracking-wide uppercase rounded-xl"
              disabled={submittingFeedback}
              data-testid="submit-feedback"
            >
              {submittingFeedback ? <div className="spinner" /> : "Send Feedback"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
